import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AUTO_DEMO_SCRIPT, type AutoDemoStep } from '../data/autoDemoScript'

const AUDIO_BASE = '/audio/auto-demo'
const HIGHLIGHT_CLASS = 'auto-demo-highlight'

interface AutoDemoState {
  isActive: boolean
  isPaused: boolean
  stepIndex: number
  totalSteps: number
  currentStep: AutoDemoStep | null
  audioMissing: boolean
  start: () => void
  pause: () => void
  resume: () => void
  next: () => void
  prev: () => void
  stop: () => void
  goToStep: (i: number) => void
}

const AutoDemoContext = createContext<AutoDemoState | null>(null)

export function AutoDemoProvider({ children }: { children: ReactNode }) {
  const [isActive, setActive] = useState(false)
  const [isPaused, setPaused] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [audioMissing, setAudioMissing] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const currentStep = isActive ? (AUTO_DEMO_SCRIPT[stepIndex] ?? null) : null

  const clearHighlights = useCallback(() => {
    document
      .querySelectorAll(`.${HIGHLIGHT_CLASS}`)
      .forEach((el) => el.classList.remove(HIGHLIGHT_CLASS))
  }, [])

  const stopInternal = useCallback(() => {
    const a = audioRef.current
    if (a) {
      a.pause()
      a.removeAttribute('src')
      a.load()
    }
    setActive(false)
    setPaused(false)
    setStepIndex(0)
    setAudioMissing(false)
    clearHighlights()
  }, [clearHighlights])

  const start = useCallback(() => {
    setActive(true)
    setPaused(false)
    setStepIndex(0)
    setAudioMissing(false)
  }, [])

  const pause = useCallback(() => {
    audioRef.current?.pause()
    setPaused(true)
  }, [])

  const resume = useCallback(() => {
    audioRef.current?.play().catch(() => {
      /* swallow — user-initiated resume */
    })
    setPaused(false)
  }, [])

  const next = useCallback(() => {
    setAudioMissing(false)
    setStepIndex((i) => {
      const nxt = i + 1
      if (nxt >= AUTO_DEMO_SCRIPT.length) {
        window.setTimeout(() => stopInternal(), 0)
        return i
      }
      return nxt
    })
  }, [stopInternal])

  const prev = useCallback(() => {
    setAudioMissing(false)
    setStepIndex((i) => Math.max(0, i - 1))
  }, [])

  const stop = useCallback(() => {
    stopInternal()
  }, [stopInternal])

  const goToStep = useCallback((i: number) => {
    const clamped = Math.max(0, Math.min(AUTO_DEMO_SCRIPT.length - 1, i))
    setAudioMissing(false)
    setStepIndex(clamped)
  }, [])

  // Side effects when current step changes — scroll, highlight, play audio.
  useEffect(() => {
    if (!isActive || !currentStep) return undefined

    if (currentStep.scrollTo) {
      const target = document.querySelector(currentStep.scrollTo)
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    let highlighted: Element | null = null
    if (currentStep.highlight) {
      highlighted = document.querySelector(currentStep.highlight)
      highlighted?.classList.add(HIGHLIGHT_CLASS)
    }

    const audio = audioRef.current
    if (audio) {
      audio.src = `${AUDIO_BASE}/${currentStep.id}.mp3`
      audio.load()
      const playPromise = audio.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          setAudioMissing(true)
        })
      }
    }

    return () => {
      highlighted?.classList.remove(HIGHLIGHT_CLASS)
    }
  }, [isActive, currentStep])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return undefined
    const onEnded = () => {
      next()
    }
    const onError = () => {
      setAudioMissing(true)
    }
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)
    return () => {
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
    }
  }, [next])

  // Fallback: when MP3 is missing or autoplay blocked, advance on an estimated
  // duration (~70ms per character of narration) so the demo still flows.
  useEffect(() => {
    if (!isActive || !currentStep || !audioMissing || isPaused) return undefined
    const estimateMs = Math.max(4000, currentStep.narration.length * 70)
    const id = window.setTimeout(() => next(), estimateMs)
    return () => window.clearTimeout(id)
  }, [isActive, currentStep, audioMissing, isPaused, next])

  const value = useMemo<AutoDemoState>(
    () => ({
      isActive,
      isPaused,
      stepIndex,
      totalSteps: AUTO_DEMO_SCRIPT.length,
      currentStep,
      audioMissing,
      start,
      pause,
      resume,
      next,
      prev,
      stop,
      goToStep,
    }),
    [
      isActive,
      isPaused,
      stepIndex,
      currentStep,
      audioMissing,
      start,
      pause,
      resume,
      next,
      prev,
      stop,
      goToStep,
    ],
  )

  return (
    <AutoDemoContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="auto" />
    </AutoDemoContext.Provider>
  )
}

export function useAutoDemo(): AutoDemoState {
  const ctx = useContext(AutoDemoContext)
  if (!ctx) throw new Error('useAutoDemo must be used inside <AutoDemoProvider>')
  return ctx
}
